import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import usePostCodes from '../../hooks/usePostCodes/index';
import Button from '../base/button';
import FormikCheckbox from '../base/forms/formikCheckbox';
import FormikFieldWrapper from '../base/forms/formikFieldWrapper';
import FormikSelect from '../base/forms/formikSelect';
import { StyledFormikField } from '../base/forms/input';
import { Column, Columns } from '../base/grid';
import { A } from '../base/typography/anchors';
import { P } from '../base/typography/typography';
import { RawLoader } from '../loading/loading';

interface Props {
	termsLink?: string;
	isSubmitting: boolean;
	values: any;
	setFieldTouched: any;
	setFieldValue: any;
}

const NewsletterFormFields: FC<Props> = ({ termsLink, isSubmitting, values, setFieldTouched, setFieldValue }) => {
	const { t } = useTranslation(['generalForm', 'newsletterForm']);
	const { postCodes, postCodesLoading, postCodesError } = usePostCodes();

	if (postCodesLoading)
		return (
			<>
				<RawLoader />
			</>
		);

	return (
		<div>
			<Columns>
				<Column tablet={postCodesError ? 1 / 2 : 1 / 3}>
					<FormikFieldWrapper name="name" languageComponent="generalForm" label={`${t('name')} *`} smallLabel>
						<StyledFormikField type="text" id="name" name="name" placeholder={t('name')} />
					</FormikFieldWrapper>
				</Column>
				<Column tablet={postCodesError ? 1 / 2 : 1 / 3}>
					<FormikFieldWrapper
						name="email"
						languageComponent="generalForm"
						label={`${t('email')} *`}
						smallLabel>
						<StyledFormikField type="text" id="email" name="email" placeholder={t('email')} />
					</FormikFieldWrapper>
				</Column>
				{!postCodesError && (
					<Column tablet={1 / 3}>
						<FormikFieldWrapper label={t('zip')} name="zip" languageComponent="registerShipment" smallLabel>
							<FormikSelect
								id="zip"
								name="zip"
								options={postCodes}
								value={values.zip}
								placeholder={t('zip')}
								onBlur={setFieldTouched}
								onChange={setFieldValue}
							/>
						</FormikFieldWrapper>
					</Column>
				)}
			</Columns>

			<Column>
				<FormikFieldWrapper name="individuals" languageComponent="generalForm">
					<FormikCheckbox
						name="individuals"
						id="individuals"
						label={t('newsletterIndividuals') as React.ReactNode}
						setFieldValue={() => {}}
					/>
				</FormikFieldWrapper>
			</Column>
			{/* TODO needs fixing in backend if we want it back in, isnt connected to the correct list in mailchipm
			 <Column>
				<FormikFieldWrapper name="shops" languageComponent="generalForm">
					<FormikCheckbox name="shops" id="shops" label={t('newsletterShops')} setFieldValue={() => {}} />
				</FormikFieldWrapper>
			</Column> */}
			<Column>
				<FormikFieldWrapper name="companies" languageComponent="generalForm">
					<FormikCheckbox
						name="companies"
						id="companies"
						label={t('newsletterCompanies') as React.ReactNode}
						setFieldValue={() => {}}
					/>
				</FormikFieldWrapper>
			</Column>

			<Column>
				<GdprWrapper>
					<P>{t('newsletterForm:gdprInfo1')}</P>
					<P>{t('newsletterForm:gdprInfo2')}</P>
					{termsLink && (
						<P>
							{t('newsletterForm:gdprInfo3')}
							<A href={termsLink}>{t('newsletterForm:gdprInfo3Link')}</A>
						</P>
					)}
					<FormikFieldWrapper name="gdpr" languageComponent="generalForm">
						<FormikCheckbox
							name="gdpr"
							id="gdpr"
							label={t('newsletterForm:gdpr') as React.ReactNode}
							setFieldValue={() => {}}
						/>
					</FormikFieldWrapper>
				</GdprWrapper>
			</Column>

			<Columns>
				<Column>
					<Button wide primary type="submit" isLoading={isSubmitting}>
						{t('signUp')}
					</Button>
				</Column>
			</Columns>
		</div>
	);
};

export default NewsletterFormFields;

const GdprWrapper = styled.div`
	margin: 2rem -1rem;
	padding: 1rem;
	background-color: ${({ theme }) => theme.colors.grayLighter};
`;
